import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, Tab, Tabs, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import { isValidInput } from "../../utils/validations";
import Pagination from "../../components/Panigation";
import { USE_FAKE_DATA } from "../../config/appConfig";

// Types
export type InvoiceStatus = 'PAID' | 'UNPAID' | 'OVERDUE';
export type PaymentMethod = 'CASH' | 'CREDIT';

export type Invoice = {
  id: string;
  invoiceNo: string;
  customer: string;
  project: string;
  bookingId?: string;
  date: string; // ISO
  amount: number;
  status: InvoiceStatus;
  erpRef: string;
};

export type Payment = {
  id: string;
  method: PaymentMethod;
  bookingId?: string;
  customer: string;
  amount: number;
  status: 'SUCCESS' | 'PENDING' | 'FAILED';
  gatewayRef?: string; // for CASH
  erpRef?: string; // for CREDIT
  date: string;
};

// Fake data
const FAKE_INVOICES: Invoice[] = [
  { id: 'i1', invoiceNo: 'INV-10001', customer: 'Acme LLC', project: 'Marina Tower', bookingId: 'BK-10001', date: '2025-11-11', amount: 12500, status: 'UNPAID', erpRef: 'ERP-INV-9001' },
  { id: 'i2', invoiceNo: 'INV-10002', customer: 'BuildCo', project: 'Expo Villas', bookingId: 'BK-10002', date: '2025-11-10', amount: 32000, status: 'PAID', erpRef: 'ERP-INV-9002' },
  { id: 'i3', invoiceNo: 'INV-10003', customer: 'Skyline', project: 'Harbour Point', bookingId: 'BK-10003', date: '2025-11-08', amount: 7800, status: 'OVERDUE', erpRef: 'ERP-INV-9003' },
];

const FAKE_PAYMENTS: Payment[] = [
  { id: 'p1', method: 'CASH', bookingId: 'BK-10001', customer: 'Acme LLC', amount: 12500, status: 'PENDING', gatewayRef: 'PG-REF-77821', date: '2025-11-11' },
  { id: 'p2', method: 'CREDIT', bookingId: 'BK-10002', customer: 'BuildCo', amount: 32000, status: 'SUCCESS', erpRef: 'ERP-PAY-11001', date: '2025-11-10' },
  { id: 'p3', method: 'CASH', bookingId: 'BK-10003', customer: 'Skyline', amount: 7800, status: 'FAILED', gatewayRef: 'PG-REF-88991', date: '2025-11-08' },
];

const FinancePage: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'INVOICES' | 'PAYMENTS' | 'REPORTS'>('INVOICES');

  // Invoices state
  const [invoiceFilter, setInvoiceFilter] = useState<'' | 'PAID' | 'UNPAID' | 'OVERDUE'>('');
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceDebounced, setInvoiceDebounced] = useState('');
  const [invPage, setInvPage] = useState(1);
  const [invLimit, setInvLimit] = useState(10);

  // Payments state
  const [payments, setPayments] = useState<Payment[]>(USE_FAKE_DATA ? FAKE_PAYMENTS : []);
  const [invoices, setInvoices] = useState<Invoice[]>(USE_FAKE_DATA ? FAKE_INVOICES : []);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundForId, setRefundForId] = useState<string | null>(null);
  const [refundAmount, setRefundAmount] = useState<string>('');

  // Filtered invoices
  const filteredInvoices = useMemo(() => {
    let data = [...invoices];
    if (invoiceFilter) data = data.filter((i) => i.status === invoiceFilter);
    const term = (invoiceDebounced || '').toLowerCase();
    if (term) {
      data = data.filter((i) =>
        i.customer.toLowerCase().includes(term) ||
        i.project.toLowerCase().includes(term) ||
        (i.bookingId || '').toLowerCase().includes(term) ||
        i.invoiceNo.toLowerCase().includes(term)
      );
    }
    return data;
  }, [invoices, invoiceFilter, invoiceDebounced]);

  const invTotal = filteredInvoices.length;
  const invStart = (invPage - 1) * invLimit;
  const invEnd = invStart + invLimit;
  const invPageItems = filteredInvoices.slice(invStart, invEnd);
  const invTotalPages = Math.max(1, Math.ceil(invTotal / (invLimit || 10)));

  useEffect(() => { setInvPage(1); }, [invoiceFilter, invoiceDebounced]);

  const statusChip = (s: InvoiceStatus) => (
    <Chip
      size="small"
      label={s}
      variant={s === 'UNPAID' ? 'outlined' : 'filled'}
      color={s === 'PAID' ? 'success' : s === 'OVERDUE' ? 'warning' : 'default'}
      sx={{ fontSize: 12, textTransform: 'uppercase' }}
    />
  );

  const formatAmount = (n: number) => new Intl.NumberFormat(undefined, { minimumFractionDigits: 0 }).format(n);

  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const [menuForId, setMenuForId] = useState<string | null>(null);
  const openMenu = Boolean(menuEl);

  const openActions = (e: React.MouseEvent<HTMLElement>, id: string) => { setMenuEl(e.currentTarget); setMenuForId(id); };
  const closeActions = () => { setMenuEl(null); setMenuForId(null); };

  const openRefund = (id: string) => { setRefundForId(id); setRefundAmount(''); setRefundOpen(true); };
  const submitRefund = () => {
    // UI-only: simulate refund
    setRefundOpen(false);
  };

  return (
    <>
      <div className="main_title">
        <h1>Financial & Payments</h1>
        <p>
          <button onClick={() => navigate('/dashboard')}>Dashboard</button> - Finance
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <Box className="custom_tabs1" sx={{ width: '100%', mb: 1 }}>
              <Tabs
                value={tab === 'INVOICES' ? 0 : tab === 'PAYMENTS' ? 1 : 2}
                onChange={(_, i) => setTab(i === 0 ? 'INVOICES' : i === 1 ? 'PAYMENTS' : 'REPORTS')}
                className="custom_tabs_links"
              >
                <Tab label="Invoices" />
                <Tab label="Payments" />
                <Tab label="Reports" />
              </Tabs>
            </Box>

            {tab === 'INVOICES' && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>

                  <Box sx={{  flexShrink: 0 }}>
                    <SearchBar
                      searchTerm={invoiceSearch}
                      placeholder="Search by Customer, Project, Booking, Invoice No..."
                      setDebouncedSearchTerm={setInvoiceDebounced}
                      value={invoiceSearch}
                      onCross={() => setInvoiceSearch('')}
                      onChange={(val: any) => { if (isValidInput(val.target.value)) setInvoiceSearch(val.target.value); }}
                    />
                  </Box>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={invoiceFilter || 'ALL'}
                    onChange={(_, val) => {
                      if (val === null) return; // keep selection
                      setInvoiceFilter(val === 'ALL' ? '' : val);
                    }}
                    sx={{
                      '& .MuiToggleButton-root': {
                        textTransform: 'uppercase',
                        borderColor: '#e2e8f0',
                        padding: '4px 10px',
                      },
                    }}
                  >
                    <ToggleButton value="ALL">All</ToggleButton>
                    <ToggleButton value="PAID">Paid</ToggleButton>
                    <ToggleButton value="UNPAID">Unpaid</ToggleButton>
                    <ToggleButton value="OVERDUE">Overdue</ToggleButton>
                  </ToggleButtonGroup>
                  <Box sx={{ flex: 1 }} />
                  <Tooltip title="Reset filters">
                    <IconButton size="small" onClick={() => USE_FAKE_DATA && setInvoices(FAKE_INVOICES)}>
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </>
            )}
          </div>
          <div className="right_s" />
        </div>

        {tab === 'INVOICES' && (
          <TableContainer className="table_container" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice No</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Booking</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>ERP Ref</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invPageItems.length ? invPageItems.map((i) => (
                  <TableRow key={i.id} hover>
                    <TableCell>{i.invoiceNo}</TableCell>
                    <TableCell>{i.customer}</TableCell>
                    <TableCell>{i.project}</TableCell>
                    <TableCell>{i.bookingId || '-'}</TableCell>
                    <TableCell>{i.date}</TableCell>
                    <TableCell align="right">{formatAmount(i.amount)}</TableCell>
                    <TableCell>{statusChip(i.status)}</TableCell>
                    <TableCell>{i.erpRef}</TableCell>
                    <TableCell>
                      <IconButton onClick={(e) => openActions(e, i.id)}>
                        <MoreVertIcon />
                      </IconButton>
                      <Menu anchorEl={menuEl} open={openMenu && menuForId === i.id} onClose={closeActions} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                        <MenuItem onClick={() => { navigate(`/finance/invoice/${i.id}`, { state: { invoice: i } }); closeActions(); }}>View</MenuItem>
                      </Menu>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={9}>No invoices found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1 }}>
              <Box sx={{ color: '#64748b', fontSize: 14 }}>Showing {invPageItems.length} of {invTotal} items</Box>
              <Pagination module={invPageItems} page={invPage} setPage={setInvPage} limit={invLimit} setLimit={setInvLimit} onPageChange={setInvPage} totalPages={invTotalPages} />
            </Box>
          </TableContainer>
        )}

        {tab === 'PAYMENTS' && (
          <TableContainer className="table_container" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Method</TableCell>
                  <TableCell>Booking</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.length ? payments.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell>{p.method}</TableCell>
                    <TableCell>{p.bookingId || '-'}</TableCell>
                    <TableCell>{p.customer}</TableCell>
                    <TableCell align="right">{formatAmount(p.amount)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={p.status} color={p.status === 'SUCCESS' ? 'success' : p.status === 'FAILED' ? 'error' : 'default'} sx={{ fontSize: 12 }} />
                    </TableCell>
                    <TableCell>{p.method === 'CASH' ? (p.gatewayRef || '-') : (p.erpRef || '-')}</TableCell>
                    <TableCell>{p.date}</TableCell>
                    <TableCell>
                      <IconButton onClick={(e) => openActions(e, p.id)}>
                        <MoreVertIcon />
                      </IconButton>
                      <Menu anchorEl={menuEl} open={openMenu && menuForId === p.id} onClose={closeActions} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                        <MenuItem onClick={() => { openRefund(p.id); closeActions(); }}>Initiate Refund</MenuItem>
                      </Menu>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={8}>No payments found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {tab === 'REPORTS' && (
          <Box sx={{ p: 2 }}>
            <h3>Reports</h3>
            <ul>
              <li>Daily collections (placeholder)</li>
              <li>Outstanding balances by customer/project (placeholder)</li>
              <li>Credit exposure vs available balance (placeholder)</li>
            </ul>
          </Box>
        )}
      </div>

      {/* Refund Dialog */}
      <Dialog open={refundOpen} onClose={() => setRefundOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Initiate Refund</DialogTitle>
        <DialogContent>
          <TextField label="Amount" type="number" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundOpen(false)}>Cancel</Button>
          <Button className="btn btn_primary" onClick={submitRefund} disabled={!refundAmount}>Submit</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FinancePage;
